import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Sprout, Flame, GraduationCap, Palette, Users, Building2, Camera, HandCoins, Globe2, BookOpen, Sparkles, Archive, Eye, CheckCircle2, Circle, TrendingUp, FileText, X, Copy } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

/**
 * Populate Console — Institutional Population Dashboard.
 *
 * Principle: Never confuse software completeness with institutional maturity.
 * Software may reach 100%. The institution may take decades. This console measures the institution.
 *
 * Priority cohort (highest leverage): Healing, Culture & Arts, Funding, Community.
 * Templates: click any item with a template icon to read the copy-paste-ready starter.
 */

const TEMPLATES = {
  "community.volunteer_sop": { title: "Volunteer & Operator SOP", path: "/institutional_templates/volunteer_sop.md" },
  "culture.residency": { title: "Artist Residency Guidelines", path: "/institutional_templates/artist_residency_guidelines.md" },
  "funding.fractured_atlas": { title: "Fractured Atlas Fiscal Sponsorship One-Pager", path: "/institutional_templates/fractured_atlas_one_pager.md" },
  "healing.curriculum": { title: "Rebirthing Genesis Curriculum Outline", path: "/institutional_templates/rebirthing_curriculum.md" },
};

// Priority departments per user direction — highest immediate leverage
const PRIORITY = ["healing", "culture", "funding", "community"];

const DEPTS = [
  { key: "healing", label: "Healing", icon: Flame, populate: [
    { k: "mission", l: "Mission statement" },
    { k: "curriculum", l: "Rebirthing Genesis curriculum", template: true },
    { k: "sops", l: "Session SOPs & intake forms" },
    { k: "facilitator_manual", l: "Facilitator training manual" },
    { k: "contraindications", l: "Contraindications & medical protocols" },
    { k: "programs", l: "Programs & retreat calendar" },
    { k: "forms", l: "Application & waiver forms" },
    { k: "media", l: "Testimonials & documentation" },
    { k: "research", l: "Research & efficacy notes" },
    { k: "partnerships", l: "Practitioner network" },
  ]},
  { key: "culture", label: "Culture & Arts", icon: Palette, populate: [
    { k: "mission", l: "Mission statement" },
    { k: "residency", l: "Artist residency guidelines", template: true },
    { k: "exchange", l: "Cultural exchange partners" },
    { k: "gallery", l: "Gallery & documentation" },
    { k: "festivals", l: "Festival calendar" },
    { k: "grants", l: "Grant opportunities database" },
    { k: "partners", l: "Partner organizations" },
    { k: "policies", l: "Cultural protocols & policies" },
    { k: "resources", l: "Studio & material resources" },
    { k: "media", l: "Artist portfolios & work archive" },
  ]},
  { key: "funding", label: "Funding", icon: HandCoins, populate: [
    { k: "grant_calendar", l: "Grant calendar" },
    { k: "grant_templates", l: "Grant application templates" },
    { k: "donor_db", l: "Donor database" },
    { k: "campaigns", l: "Active campaigns" },
    { k: "sponsors", l: "Sponsor register" },
    { k: "fractured_atlas", l: "Fractured Atlas sponsorship one-pager", template: true },
    { k: "major_gifts", l: "Major gifts framework" },
    { k: "reporting", l: "Impact reporting cadence" },
    { k: "budget", l: "Operating budget" },
    { k: "metrics", l: "Impact metrics dashboard" },
  ]},
  { key: "community", label: "Community", icon: Users, populate: [
    { k: "volunteer_sop", l: "Volunteer & Operator SOP", template: true },
    { k: "member_handbook", l: "Member handbook" },
    { k: "arrival", l: "Arrival process & packet" },
    { k: "conflict", l: "Conflict resolution protocol" },
    { k: "housing", l: "Housing assignment system" },
    { k: "code_of_conduct", l: "Code of Conduct" },
    { k: "departure", l: "Departure ritual & alumni handoff" },
    { k: "alumni", l: "Alumni network directory" },
    { k: "circles", l: "Circle facilitation templates" },
    { k: "safety", l: "Emergency & safety procedures" },
  ]},
  { key: "ecology", label: "Ecology", icon: Sprout, populate: [
    { k: "land_maps", l: "Land maps & parcels" },
    { k: "food_forest", l: "Food forest plan & species" },
    { k: "water_systems", l: "Water systems documentation" },
    { k: "planting_calendar", l: "Seasonal planting calendar" },
    { k: "seeds", l: "Seed inventory" },
    { k: "animals", l: "Animal stewardship plan" },
    { k: "nursery", l: "Nursery operations" },
    { k: "watershed", l: "Watershed restoration plan" },
    { k: "bamboo", l: "Bamboo grove management" },
    { k: "wildlife", l: "Wildlife corridor documentation" },
  ]},
  { key: "education", label: "Education", icon: GraduationCap, populate: [
    { k: "guilds", l: "Guild descriptions" },
    { k: "curriculum", l: "Course curricula" },
    { k: "reading_lists", l: "Reading lists" },
    { k: "teachers", l: "Teacher roster & bios" },
    { k: "seasonal_intensives", l: "Seasonal intensives calendar" },
    { k: "apprenticeships", l: "Apprenticeship program" },
    { k: "certifications", l: "Certification frameworks" },
    { k: "library", l: "Library & reading room" },
    { k: "online_learning", l: "Online learning roadmap" },
    { k: "assessment", l: "Learner assessment approach" },
  ]},
  { key: "development", label: "Development", icon: Building2, populate: [
    { k: "master_plan", l: "Dubbi master plan" },
    { k: "architecture", l: "Architectural drawings" },
    { k: "construction_sop", l: "Construction SOPs" },
    { k: "infrastructure", l: "Infrastructure documentation" },
    { k: "materials", l: "Materials sourcing (bamboo, earth, stone)" },
    { k: "housing_models", l: "Tiny house models" },
    { k: "reservations", l: "Reservation & investment pipeline" },
    { k: "land_expansion", l: "Land expansion strategy" },
    { k: "permits", l: "Permits & legal framework" },
    { k: "budget", l: "Build budget by phase" },
  ]},
  { key: "media", label: "Media", icon: Camera, populate: [
    { k: "brand_assets", l: "Brand assets & style guide" },
    { k: "photo_archive", l: "Photo archive (organized)" },
    { k: "video_archive", l: "Video archive" },
    { k: "podcast", l: "Podcast production pipeline" },
    { k: "newsletter", l: "Newsletter cadence & template" },
    { k: "social", l: "Social media standards" },
    { k: "books", l: "Book publishing pipeline" },
    { k: "documentary", l: "Documentary production plan" },
    { k: "press_kit", l: "Press kit" },
    { k: "attribution", l: "Attribution & usage rights" },
  ]},
  { key: "research", label: "Research", icon: BookOpen, populate: [
    { k: "medicinal_plants", l: "Medicinal plant database" },
    { k: "case_studies", l: "Case studies" },
    { k: "traditional_knowledge", l: "Traditional knowledge documentation" },
    { k: "building_systems", l: "Building systems research" },
    { k: "healing_research", l: "Healing modality research" },
    { k: "publications", l: "Publications list" },
    { k: "fellows", l: "Research fellows directory" },
    { k: "methodology", l: "Research methodology & ethics" },
    { k: "partnerships", l: "University & research partners" },
    { k: "irc_charter", l: "Institute for Regenerative Civilization charter" },
  ]},
  { key: "alliance", label: "Global Alliance", icon: Globe2, populate: [
    { k: "partner_orgs", l: "Partner organizations register" },
    { k: "partner_sanctuaries", l: "Partner sanctuaries" },
    { k: "universities", l: "University partnerships" },
    { k: "delegations", l: "Delegation history" },
    { k: "cultural_exchange", l: "Cultural exchange framework" },
    { k: "indigenous", l: "Indigenous community partnerships" },
    { k: "diplomacy", l: "Diplomatic protocols" },
    { k: "conferences", l: "Conferences & gatherings hosted" },
    { k: "mou", l: "MOU templates" },
    { k: "annual_gathering", l: "Annual global gathering plan" },
  ]},
  { key: "administration", label: "Administration", icon: Sparkles, populate: [
    { k: "charter", l: "Institutional Charter", auto: true },
    { k: "governance", l: "Governance documents" },
    { k: "legal", l: "Legal framework" },
    { k: "financial", l: "Financial systems" },
    { k: "strategic_plan", l: "Strategic plan" },
    { k: "org_chart", l: "Organizational chart" },
    { k: "policies", l: "Policies & procedures" },
    { k: "annual_report", l: "Annual report template" },
    { k: "insurance", l: "Insurance & risk management" },
    { k: "tax_status", l: "Tax status & registrations" },
  ]},
  { key: "archives", label: "Archives & Legacy", icon: Archive, populate: [
    { k: "founding_docs", l: "Founding documents" },
    { k: "vision_papers", l: "Vision papers" },
    { k: "land_history", l: "Land history" },
    { k: "oral_histories", l: "Oral histories" },
    { k: "annual_reports", l: "Annual reports" },
    { k: "major_decisions", l: "Major decisions log (with rationale)" },
    { k: "photo_history", l: "Historical photo archive" },
    { k: "design_iterations", l: "Design iterations record" },
    { k: "curriculum_versions", l: "Curriculum version history" },
    { k: "grant_history", l: "Grant history" },
  ]},
  { key: "observatory", label: "Observatory · Learning & Evaluation", icon: Eye, populate: [
    { k: "indicators", l: "Impact indicators framework" },
    { k: "story_collection", l: "Story collection protocol" },
    { k: "program_eval", l: "Program evaluation methodology" },
    { k: "ecological_baseline", l: "Ecological baseline & tracking" },
    { k: "human_development", l: "Human development observation" },
    { k: "annual_report", l: "Annual State of the Institution" },
    { k: "learning_log", l: "Institutional learning log (succeeded / failed / learned)" },
    { k: "feedback_loop", l: "Knowledge feedback loop to departments" },
    { k: "ethics", l: "Observation ethics & privacy protocol" },
    { k: "reflection_cadence", l: "Quarterly reflection cadence" },
  ]},
];

// Software Completeness — pre-computed (what the CODE provides).
const SOFTWARE_COMPLETE = [
  { l: "Public Website" }, { l: "Institutional Map" }, { l: "Charter" }, { l: "12 Departments" },
  { l: "CRM" }, { l: "Projects" }, { l: "Applications" }, { l: "Donations (Stripe)" },
  { l: "Volunteer Portal" }, { l: "Admin Dashboard" }, { l: "Grant Center" }, { l: "Campaign Center" },
  { l: "Media Library" }, { l: "Analytics" }, { l: "Auth (JWT + Google)" }, { l: "Object Storage" },
];

export default function Populate() {
  const [stats, setStats] = useState(null);
  const [checked, setChecked] = useState(() => {
    try { return JSON.parse(localStorage.getItem("neos_populate_checks") || "{}"); } catch { return {}; }
  });
  const [templateOpen, setTemplateOpen] = useState(null);
  const [templateText, setTemplateText] = useState("");

  useEffect(() => { api.get("/dashboard/stats").then(r => setStats(r.data)).catch(() => {}); }, []);

  const toggle = (deptKey, itemKey) => {
    const id = `${deptKey}.${itemKey}`;
    const next = { ...checked, [id]: !checked[id] };
    setChecked(next);
    localStorage.setItem("neos_populate_checks", JSON.stringify(next));
  };

  const isAuto = (dept, item) => item.auto === true;

  const openTemplate = async (dept, item) => {
    const key = `${dept}.${item.k}`;
    const meta = TEMPLATES[key];
    if (!meta) return;
    setTemplateOpen(meta);
    try {
      const res = await fetch(meta.path);
      const text = await res.text();
      setTemplateText(text);
    } catch {
      setTemplateText("Template failed to load.");
    }
  };

  const deptProgress = (dept) => {
    const total = dept.populate.length;
    const done = dept.populate.filter(p => checked[`${dept.key}.${p.k}`] || isAuto(dept.key, p)).length;
    return { done, total, pct: total ? Math.round((done/total)*100) : 0 };
  };

  const overall = useMemo(() => {
    return DEPTS.reduce((acc, d) => {
      const p = deptProgress(d);
      return { done: acc.done + p.done, total: acc.total + p.total };
    }, { done: 0, total: 0 });
  }, [checked]);
  const overallPct = overall.total ? Math.round((overall.done / overall.total) * 100) : 0;

  // Missing items — surface top 8 across priority departments first
  const missing = useMemo(() => {
    const list = [];
    for (const d of DEPTS) {
      for (const item of d.populate) {
        const key = `${d.key}.${item.k}`;
        if (!checked[key] && !isAuto(d.key, item)) {
          list.push({ dept: d, item, key, priority: PRIORITY.includes(d.key) });
        }
      }
    }
    return list.sort((a, b) => (a.priority === b.priority ? 0 : a.priority ? -1 : 1));
  }, [checked]);

  const sortedDepts = [...DEPTS].sort((a, b) => {
    const pA = PRIORITY.includes(a.key), pB = PRIORITY.includes(b.key);
    return pA === pB ? 0 : pA ? -1 : 1;
  });

  return (
    <div className="space-y-8">
      <div>
        <div className="overline">Executive · Institutional Stewardship</div>
        <h1 className="font-serif text-4xl mt-1">Institutional Population</h1>
        <p className="text-ink-soft mt-3 max-w-3xl leading-relaxed">
          Every document you create is stewardship. Every curriculum, every artist residency, every volunteer handbook, every grant, every observation — stewardship. This console measures the institution — not the code.
        </p>
      </div>

      {/* Guiding principles */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="rounded-xl bg-forest text-bone p-6" data-testid="principle-sequence">
          <div className="overline text-bone/70 mb-2">Guiding Sequence</div>
          <div className="font-serif text-lg leading-tight">Knowledge precedes memory. Memory precedes intelligence. Intelligence precedes autonomy.</div>
        </div>
        <div className="rounded-xl border-2 border-terracotta/40 bg-terracotta/[0.06] p-6" data-testid="principle-two-progress">
          <div className="overline text-terracotta mb-2">Guiding Principle</div>
          <div className="font-serif text-lg leading-tight text-ink">Never confuse software completeness with institutional maturity. Software may reach 100%. The institution may take decades.</div>
        </div>
      </div>

      {/* Two progress bars */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-admin-surface border border-line rounded-xl p-6" data-testid="progress-software">
          <div className="flex items-center justify-between mb-3">
            <div className="overline">Software Completeness</div>
            <div className="font-serif text-3xl text-forest">100%</div>
          </div>
          <div className="h-2 bg-line rounded-full overflow-hidden mb-3"><div className="h-full bg-forest" style={{ width: "100%" }} /></div>
          <div className="text-xs text-ink-soft grid grid-cols-2 gap-1 mt-2">
            {SOFTWARE_COMPLETE.map(s => <div key={s.l} className="flex items-center gap-1"><CheckCircle2 className="h-3 w-3 text-forest shrink-0" strokeWidth={2} />{s.l}</div>)}
          </div>
        </div>

        <div className="bg-admin-surface border-2 border-terracotta rounded-xl p-6" data-testid="progress-institutional">
          <div className="flex items-center justify-between mb-3">
            <div className="overline">Institutional Maturity</div>
            <div className="font-serif text-3xl text-terracotta">{overallPct}%</div>
          </div>
          <div className="h-2 bg-line rounded-full overflow-hidden mb-3"><div className="h-full bg-terracotta transition-all duration-700" style={{ width: `${overallPct}%` }} /></div>
          <p className="text-xs text-ink-soft">{overall.done} of {overall.total} foundational items across 12 departments. This is the real measure.</p>
        </div>
      </div>

      {/* Live evidence */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { l: "Contacts", v: stats.totals?.contacts },
            { l: "Media files", v: stats.totals?.media },
            { l: "Projects", v: stats.totals?.projects },
            { l: "Grants tracked", v: (stats.grant_pipeline?.prospect || 0) + (stats.grant_pipeline?.drafting || 0) + (stats.grant_pipeline?.submitted || 0) + (stats.grant_pipeline?.awarded || 0) },
          ].map(s => (
            <div key={s.l} className="bg-bone/50 border border-line rounded-lg px-4 py-3 text-xs" data-testid={`populate-live-${s.l.toLowerCase().replace(/\s+/g,'-')}`}>
              <div className="overline">{s.l} in NEOS</div>
              <div className="font-serif text-2xl text-ink mt-1">{s.v ?? 0}</div>
            </div>
          ))}
        </div>
      )}

      {/* Next Actions — actionable, priority-first */}
      <div className="bg-admin-surface border border-line rounded-xl p-6" data-testid="next-actions">
        <div className="flex items-center gap-2 overline mb-4"><TrendingUp className="h-3.5 w-3.5 text-terracotta" strokeWidth={1.5} /> What&apos;s needed next</div>
        {missing.length ? (
          <ul className="space-y-2">
            {missing.slice(0, 8).map(({ dept, item, key, priority }) => (
              <li key={key} className="flex items-center justify-between text-sm py-2 border-b border-line last:border-b-0">
                <div className="flex items-center gap-3 min-w-0">
                  <dept.icon className="h-4 w-4 text-terracotta shrink-0" strokeWidth={1.5} />
                  <div className="min-w-0">
                    <div className="text-ink truncate">{item.l}</div>
                    <div className="overline">{dept.label} {priority && <span className="ml-2 text-terracotta">· priority</span>}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {item.template && <button onClick={() => openTemplate(dept.key, item)} data-testid={`next-template-${dept.key}-${item.k}`} className="text-xs edlink text-forest flex items-center gap-1"><FileText className="h-3 w-3" /> Template</button>}
                  <button onClick={() => toggle(dept.key, item.k)} data-testid={`next-check-${dept.key}-${item.k}`} className="text-xs edlink text-forest">Mark done</button>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-ink-soft text-sm">The institution is fully populated. Sprint D is ready to activate.</div>
        )}
      </div>

      {/* Departments — priority first */}
      <div className="grid md:grid-cols-2 gap-5">
        {sortedDepts.map((d) => {
          const p = deptProgress(d);
          const isPriority = PRIORITY.includes(d.key);
          return (
            <div key={d.key} className={`bg-admin-surface border rounded-xl p-6 ${isPriority ? "border-terracotta/60" : "border-line"}`} data-testid={`populate-dept-${d.key}`}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-full grid place-items-center ${isPriority ? "bg-terracotta text-bone" : "bg-forest/10 text-forest"}`}>
                    <d.icon className="h-4 w-4" strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="font-serif text-xl leading-tight flex items-center gap-2">{d.label} {isPriority && <span className="text-[9px] uppercase tracking-widest text-terracotta">Priority</span>}</div>
                    <div className="overline mt-0.5">{p.done}/{p.total} · {p.pct}%</div>
                  </div>
                </div>
              </div>
              <div className="h-1.5 bg-line rounded-full overflow-hidden mb-4">
                <div className={`h-full transition-all duration-500 ${isPriority ? "bg-terracotta" : "bg-forest"}`} style={{ width: `${p.pct}%` }} />
              </div>
              <ul className="space-y-2">
                {d.populate.map((item) => {
                  const done = checked[`${d.key}.${item.k}`] || isAuto(d.key, item);
                  const hasTemplate = !!TEMPLATES[`${d.key}.${item.k}`];
                  return (
                    <li key={item.k} className="flex items-center gap-2">
                      <button onClick={() => !isAuto(d.key, item) && toggle(d.key, item.k)} disabled={isAuto(d.key, item)}
                        data-testid={`populate-item-${d.key}-${item.k}`}
                        className={`flex items-start gap-2 text-left text-sm py-0.5 rounded flex-1 min-w-0 ${isAuto(d.key, item) ? "cursor-default" : "hover:text-forest"}`}>
                        {done ? <CheckCircle2 className="h-4 w-4 text-terracotta shrink-0 mt-0.5" strokeWidth={1.5} /> : <Circle className="h-4 w-4 text-ink-soft shrink-0 mt-0.5" strokeWidth={1.5} />}
                        <span className={done ? "text-ink line-through decoration-terracotta/40" : "text-ink-soft"}>
                          {item.l}
                          {isAuto(d.key, item) && <span className="ml-2 text-[10px] uppercase tracking-widest text-forest">Live</span>}
                        </span>
                      </button>
                      {hasTemplate && (
                        <button onClick={() => openTemplate(d.key, item)} data-testid={`template-${d.key}-${item.k}`} className="text-xs text-forest hover:text-terracotta shrink-0" title="Open starter template">
                          <FileText className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>

      <div className="rounded-2xl border border-dashed border-forest/30 bg-forest/[0.03] p-8">
        <div className="overline mb-2">Next</div>
        <div className="font-serif text-2xl text-ink leading-tight">Sprint D (Knowledge Vault) activates when Institutional Maturity crosses <span className="text-terracotta">60%</span>.</div>
        <p className="text-ink-soft mt-2 text-sm">You are not building librarians before there are books. Sprint E (AI Staff) activates only after Sprint D holds real institutional knowledge worth remembering.</p>
      </div>

      {/* Institutional Body Legend */}
      <div className="rounded-2xl bg-forest text-bone p-8" data-testid="institutional-body">
        <div className="overline text-bone/70 mb-3">The Institutional Body</div>
        <div className="text-bone/90 text-sm grid md:grid-cols-2 gap-x-6 gap-y-1">
          <div>NEOS — the nervous system</div>
          <div>Celestial Seed Sanctuary — the heart</div>
          <div>People — the life force</div>
          <div>Culture &amp; Arts — the soul</div>
          <div>The Charter — the DNA</div>
          <div>Departments — the organs</div>
          <div>Projects — the hands and feet</div>
          <div>Global Alliance — the circulatory system</div>
          <div>Knowledge Vault — the memory</div>
          <div>AI Staff — the executive function</div>
          <div>Archives &amp; Legacy — the bones</div>
          <div>Research — the curiosity</div>
          <div>Observatory — the eyes, ears, and mirror</div>
          <div className="text-terracotta">The Land — the living teacher</div>
        </div>
        <div className="mt-6 pt-4 border-t border-bone/20">
          <div className="overline text-bone/70 mb-2">Architectural Status</div>
          <div className="font-serif text-xl leading-tight">Institutional architecture is now complete. The work shifts from expanding the structure to deepening its substance.</div>
        </div>
      </div>

      {/* Template modal */}
      <Dialog open={!!templateOpen} onOpenChange={(v) => { if (!v) { setTemplateOpen(null); setTemplateText(""); } }}>
        <DialogContent className="bg-bone max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <div className="overline mb-1">Starter Template · Copy · Adapt · Save</div>
            <DialogTitle className="font-serif text-2xl leading-tight">{templateOpen?.title}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto bg-surface border border-line rounded-lg p-6 my-4">
            <pre className="whitespace-pre-wrap font-serif text-sm text-ink leading-relaxed">{templateText || "Loading…"}</pre>
          </div>
          <div className="flex gap-3">
            <button onClick={() => { navigator.clipboard.writeText(templateText); toast.success("Template copied — paste into Notion, Docs, or your Knowledge Vault"); }} data-testid="template-copy" className="pill pill-primary text-sm"><Copy className="h-3.5 w-3.5" /> Copy Template</button>
            <button onClick={() => { setTemplateOpen(null); setTemplateText(""); }} className="pill pill-outline text-sm"><X className="h-3.5 w-3.5" /> Close</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
